<template>
    <div class="container">
        <o-bread-crumb>
            <template v-slot:pageName>Newsletter</template>
            <template v-slot:pageDetail>{{ isEdit ? 'Edit' : 'Create' }}</template>
        </o-bread-crumb>

        <div v-if="!isNewsletterModuleEnabled" class="feature-disabled-container">
            <div class="disabled-content">
                <i class="fas fa-toggle-off fa-3x primary-text mb-3"></i>
                <h4>Newsletter Module Disabled</h4>
                <p>The Newsletter Module has been disabled by System Administration. Please contact System Owner to enable this feature.</p>
            </div>
        </div>

        <div class="row promotions-container" v-if="thisComponentShow && isNewsletterModuleEnabled">
            <!-- Left Side: Form -->
            <div class="col-lg-7">
                <div class="promotions-card">
                    <div class="promotions-header">
                        <div class="header-left">
                            <h4 class="header-title">
                                <i class="fas fa-newspaper me-2 text-primary"></i>
                                {{ isEdit ? 'Edit Newsletter' : 'Create Newsletter' }}
                            </h4>
                            <p class="header-subtitle">Write and design your newsletter content</p>
                        </div>
                    </div>

                    <!-- Content -->
                    <div class="promotions-content">
                        <toast-notification 
                            v-model="toast.show" 
                            :type="toast.type" 
                            :message="toast.message" 
                            :title="toast.title" 
                        />

                        <!-- Form Section -->
                        <div class="settings-section">
                            <form @submit.prevent="handleSubmit">
                                <div class="row mb-2">
                                    <div class="col-12">
                                        <label class="section-label required">Title</label>
                                        <input 
                                            type="text" 
                                            class="form-control"
                                            :class="{ 'is-invalid': errors.title }"
                                            v-model="form.title"
                                            placeholder="Enter newsletter title"
                                            maxlength="150"
                                        >
                                        <div class="invalid-feedback" v-if="errors.title">{{ errors.title }}</div>
                                        <div class="d-flex justify-content-end">
                                            <small class="char-counter">{{ (form.title || '').length }}/150 characters</small>
                                        </div>
                                    </div>
                                </div>

                                <div class="row mb-2">
                                    <div class="col-12">
                                        <label class="section-label required">Summary</label>
                                        <textarea 
                                            class="form-control"
                                            :class="{ 'is-invalid': errors.summary }"
                                            v-model="form.summary"
                                            placeholder="Brief summary of the newsletter"
                                            rows="2"
                                            maxlength="250"
                                        ></textarea>
                                        <div class="invalid-feedback" v-if="errors.summary">{{ errors.summary }}</div>
                                        <div class="d-flex justify-content-end">
                                            <small class="char-counter">{{ (form.summary || '').length }}/250 characters</small>
                                        </div>
                                    </div>
                                </div>

                                <div class="row mb-4">
                                    <div class="col-12">
                                        <label class="section-label required">Content</label>
                                        <OTextEditor
                                            v-model="form.body"
                                            placeholder="Write your newsletter content here..."
                                        />
                                        <div class="text-danger small mt-1" v-if="errors.body">{{ errors.body }}</div>
                                    </div>
                                </div>

                                <div class="row mb-4">
                                    <div class="col-12">
                                        <label class="section-label">Cover Image</label>
                                        <div class="position-relative d-inline-block w-100">
                                            <OImageInput 
                                                id="cover-upload"
                                                name="cover_upload"
                                                class="w-100"
                                                :imageurl="coverPreview || form.cover_image"
                                                :skipCompression="true"
                                                @selectImage="handleImageSelect"
                                                ref="imageInputRef"
                                            />
                                            <button v-if="coverPreview || form.cover_image" type="button" class="btn-remove-cover-absolute" @click="removeCover" title="Remove cover image">
                                                <i class="fas fa-times"></i>
                                            </button>
                                        </div>
                                        <div class="text-danger small mt-1" v-if="errors.cover_image">{{ errors.cover_image }}</div>
                                    </div>
                                </div>

                                <div class="row mb-4">
                                    <div class="col-12">
                                        <label class="section-label">Target Audience</label>
                                        <div class="tier-selection">
                                            <div class="form-check mb-2">
                                                <input 
                                                    class="form-check-input" 
                                                    type="checkbox" 
                                                    id="allTiers"
                                                    v-model="allTiersSelected"
                                                    @change="toggleAllTiers"
                                                    :disabled="form.status === 'published'"
                                                >
                                                <label class="form-check-label fw-medium" for="allTiers">
                                                    All Member Tiers <span v-if="form.status === 'published'" class="text-danger small ms-2">(Cannot be changed after publishing)</span>
                                                </label>
                                            </div>
                                            <div class="tier-list" v-if="!allTiersSelected">
                                                <p class="tier-instruction opacity-80 small fw-medium mb-2 mt-1">
                                                    Select specific tiers to target members:
                                                </p>
                                                <div 
                                                    class="form-check" 
                                                    v-for="tier in tiers" 
                                                    :key="tier.id"
                                                >
                                                    <input 
                                                        class="form-check-input" 
                                                        type="checkbox" 
                                                        :id="`tier-${tier.id}`"
                                                        :value="tier.id"
                                                        v-model="form.tier_ids"
                                                        :disabled="form.status === 'published'"
                                                    >
                                                    <label class="form-check-label" :for="`tier-${tier.id}`">
                                                        {{ tier.name }} ({{ tier.member_tier_count }} Member{{ tier.member_tier_count !== 1 ? 's' : '' }})
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <!-- Form Actions -->
                                <div class="form-actions d-flex justify-content-between mt-5 pt-3 border-top">
                                    <button type="button" class="btn-secondary-action action-xl" @click="goBack">
                                        <i class="fas fa-arrow-left me-2"></i>Cancel
                                    </button>
                                    <button 
                                        type="submit"
                                        class="btn-primary-action action-xl" 
                                        :disabled="page.isSaving"
                                    >
                                        <span v-if="page.isSaving" class="spinner-border spinner-border-sm me-2"></span>
                                        <i v-else class="fas fa-save me-2"></i>
                                        {{ page.isSaving ? 'Saving...' : (isEdit ? 'Update' : 'Save as Draft') }}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Right Side: Mobile Preview -->
            <div class="col-lg-5 d-none d-lg-block">
                <div class="sticky-top" style="top: 20px; z-index: 10;">
                    <div class="promotions-card preview-card shadow-sm">
                        <div class="promotions-header">
                            <div class="header-left">
                                <h4 class="header-title">
                                    <i class="fas fa-mobile-alt me-2 text-primary"></i>Live Preview
                                </h4>
                                <p class="header-subtitle">Real-time mobile display reflection</p>
                            </div>
                        </div>
                        <div class="promotions-content preview-inner bg-white">
                            <mobile-preview :newsletter="previewData" />
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="!thisComponentShow" class="mt-5">
            <o-warning-box
                title="Permission Denied"
                message="You do not have required permission for newsletter"
                action-text="Go to Home"
                cancel-text="Back"
                :hide-cancel="true"
                @action="$router.push({ name: 'Home' })"
            />
        </div>
    </div>
</template>


<script>
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { useRouter, useRoute } from 'vue-router'
import { OBreadCrumb, OWarningBox, OImageInput } from '@/components/OmnihControl'
import OTextEditor from '@/components/OmnihControl/OTextEditor.vue'
import MobilePreview from './NewsletterPreview.vue'
import ToastNotification from '@/components/member/ToastNotification.vue'

export default {
    name: 'NewsletterForm',
    components: {
        OBreadCrumb,
        MobilePreview,
        ToastNotification,
        OWarningBox,
        OTextEditor,
        OImageInput
    },
    setup() {
        const store = useStore()
        const router = useRouter()
        const route = useRoute()

        const toast = reactive({
            show: false,
            message: '',
            type: 'success',
            title: ''
        })

        function triggerToast(message, type = 'success', title = '') {
            toast.message = message
            toast.type = type
            toast.title = title
            toast.show = true
        }

        const page = reactive({
            isLoading: false,
            isSaving: false,
            component: 'newsletter'
        })

        const write = ref(true)
        const thisComponentShow = ref(true)

        const form = reactive({
            title: '',
            summary: '',
            body: '',
            cover_image: null,
            tier_ids: [],
            status: 'draft'
        })

        const errors = reactive({
            title: '',
            summary: '',
            body: '',
            cover_image: ''
        })

        const imageInputRef = ref(null)
        const coverFile = ref(null)
        const coverPreview = ref(null)
        const allTiersSelected = ref(true)

        const isEdit = computed(() => !!route.params.id)
        const tiers = computed(() => store.state.Newsletter.tiers || [])

        // Check if newsletter module is enabled via feature flag
        const isNewsletterModuleEnabled = computed(() => {
            return (store.state.FeatureFlag?.newsletterModule?.flag || 'ACTIVE') === 'ACTIVE'
        })

        const previewData = computed(() => ({
            title: form.title || 'Newsletter Title',
            summary: form.summary,
            body: form.body || 'Your newsletter content will appear here...',
            cover_image: coverPreview.value || form.cover_image,
            created_at: new Date().toISOString()
        }))

        // Auto-select "All Tiers" if all specific tiers are checked manually
        watch(() => form.tier_ids, (newIds) => {
            if (!allTiersSelected.value && tiers.value.length > 0 && newIds.length === tiers.value.length) {
                allTiersSelected.value = true
                form.tier_ids = [] // switched to "All" mode
            }
        })

        function clearErrors() {
            errors.title = ''
            errors.summary = ''
            errors.body = ''
            errors.cover_image = ''
        }

        function validate() {
            clearErrors()
            let isValid = true

            if (!form.title.trim()) {
                errors.title = 'Title is required'
                isValid = false
            }

            if (!form.summary || !form.summary.trim()) {
                errors.summary = 'Summary is required'
                isValid = false
            }

            const strippedBody = form.body.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim()
            
            if (!strippedBody) {
                errors.body = 'Content is required'
                isValid = false
            }

            return isValid
        }

        const checkUserPermissions = () => {
            if (store.state.Auth.currUser.level === 'root') {
                write.value = true
                return
            }

            const userModules = store.state.Auth.userInfo.modules
            if (userModules) {
                const matchingModule = userModules.find(module => module.module_name === page.component)
                if (matchingModule) {
                    write.value = matchingModule.write
                } else {
                    write.value = false
                    thisComponentShow.value = false
                }
            } else {
                write.value = false
                thisComponentShow.value = false
            }
        }

        async function loadTiers() {
            try {
                await store.dispatch('Newsletter/readTiers')
            } catch (err) {
                console.error('Failed to load tiers:', err)
            }
        }

        async function loadNewsletter() {
            if (!isEdit.value) return

            page.isLoading = true
            try {
                const newsletter = await store.dispatch('Newsletter/readSingle', route.params.id)
                form.title = newsletter.title || ''
                form.summary = newsletter.summary || ''
                form.body = newsletter.body || ''
                form.cover_image = newsletter.cover_image || null
                form.status = newsletter.status || 'draft'
                
                if (newsletter.tiers && newsletter.tiers.length > 0) {
                    allTiersSelected.value = false
                    form.tier_ids = newsletter.tiers.map(t => t.id)
                } else {
                    allTiersSelected.value = true
                    form.tier_ids = []
                }
            } catch (err) {
                triggerToast(err.response?.data?.message || 'Failed to load newsletter', 'error', 'Error')
            } finally {
                page.isLoading = false
            }
        }

        function handleImageSelect(base64Image) {
            if (!base64Image) return;

            // Compress Image logic (Resize & Quality)
            // This reduces payload size significantly to avoid Nginx 413 errors
            const img = new Image();
            img.src = base64Image;
            img.onload = () => {
                const canvas = document.createElement('canvas');
                // Max width 1024px is sufficient for newsletter covers
                const MAX_WIDTH = 1024; 
                let width = img.width;
                let height = img.height;

                if (width > MAX_WIDTH) {
                    height *= MAX_WIDTH / width;
                    width = MAX_WIDTH;
                }

                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, width, height);

                // Compress to JPEG with 0.7 quality
                // This creates a much smaller Base64 string
                const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
                
                coverFile.value = dataUrl;
                coverPreview.value = dataUrl;
                errors.cover_image = '';
            }
            img.onerror = () => {
                console.error('Error processing image.')
                errors.cover_image = 'Failed to process image'
            }
        }

        function removeCover() {
            coverFile.value = null
            coverPreview.value = null
            form.cover_image = null
            if (imageInputRef.value) {
                imageInputRef.value.removeImage()
            }
        }

        function toggleAllTiers() {
            if (allTiersSelected.value) {
                form.tier_ids = []
            }
        }

        async function handleSubmit() {
            if (!validate()) return

            page.isSaving = true
            try {
                const formData = new FormData()
                formData.append('title', form.title)
                formData.append('summary', form.summary || '')
                formData.append('body', form.body)
                
                if (coverFile.value) {
                    formData.append('cover_image', coverFile.value)
                }

                // Send is_all_tiers and target_tiers for tier targeting
                formData.append('is_all_tiers', allTiersSelected.value ? '1' : '0')
                
                if (!allTiersSelected.value && form.tier_ids.length > 0) {
                    form.tier_ids.forEach((id, index) => {
                        formData.append(`target_tiers[${index}]`, id)
                    })
                }

                if (isEdit.value) {
                    formData.append('_method', 'PUT')
                    await store.dispatch('Newsletter/update', { 
                        id: route.params.id, 
                        data: formData 
                    })
                } else {
                    await store.dispatch('Newsletter/create', formData)
                }

                triggerToast('Newsletter saved as draft successfully!', 'success', 'Saved')

                setTimeout(() => {
                    router.push({ name: 'Newsletter' })
                }, 1000)
            } catch (err) {
                triggerToast(err.response?.data?.message || 'Failed to save newsletter', 'error', 'Error')
                
                // Handle validation errors from API
                if (err.response?.data?.errors) {
                    const apiErrors = err.response.data.errors
                    if (apiErrors.title) errors.title = apiErrors.title[0]
                    if (apiErrors.body) errors.body = apiErrors.body[0]
                    if (apiErrors.cover_image) errors.cover_image = apiErrors.cover_image[0]
                }
            } finally {
                page.isSaving = false
            }
        }

        function goBack() {
            router.push({ name: 'Newsletter' })
        }

        onMounted(() => {
            if (store.state.Auth.selfLoaded) {
                checkUserPermissions()
                loadTiers()
                loadNewsletter()
            }
        })

        watch(() => store.state.Auth.selfLoaded, (newVal) => {
            if (newVal) {
                checkUserPermissions()
                loadTiers()
                loadNewsletter()
            }
        })

        return {
            toast,
            page,
            write,
            thisComponentShow,
            form,
            errors,
            imageInputRef,
            coverPreview,
            allTiersSelected,
            isEdit,
            tiers,
            previewData,
            handleImageSelect,
            removeCover,
            toggleAllTiers,
            handleSubmit,
            goBack,
            isNewsletterModuleEnabled
        }
    }
}
</script>


<style scoped>
/* Feature Disabled State */
.feature-disabled-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 400px;
    background: #ffffff;
    border-radius: 12px;
    border: 1px solid #e5e7eb;
    padding: 2rem;
}

.disabled-content {
    text-align: center;
    color: #6b7280;
}

.disabled-content h4 {
    color: #374151;
    margin-bottom: 0.5rem;
    font-weight: 600;
}

.disabled-content p {
    margin: 0;
    font-size: 0.95rem;
}

.promotions-container {
    margin-top: 1rem;
}

.text-primary {
    color: var(--primary-color, #f27178) !important;
}

/* Card Styles (from Promotions.vue) */
.promotions-card {
    background: #fff;
    border-radius: 16px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    overflow: hidden;
    margin-bottom: 24px;
}

.promotions-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 1rem;
    padding: 20px 24px;
    border-bottom: 1px solid #f0f0f0;
}

.header-left {
    flex: 1;
    min-width: 200px;
}

.header-title {
    font-size: 1.25rem;
    font-weight: 600;
    color: #1a1a2e;
    margin-bottom: 4px;
}

.header-subtitle {
    font-size: 0.875rem;
    color: #6B7280;
    margin: 0;
}

.header-actions {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
}

.promotions-content {
    padding: 24px;
}

/* Form Control Styling */
.form-control {
    border-radius: 10px;
    border: 1px solid #d1d5db;
    padding: 10px 14px;
    font-size: 0.95rem;
    color: #1f2937;
    transition: all 0.2s ease;
}

.form-control:focus {
    border-color: var(--primary-color, #f27178);
    box-shadow: 0 0 0 4px var(--secondary-color, rgba(242, 113, 120, 0.25));
    outline: none;
}

.form-control::placeholder {
    color: #9ca3af;
    opacity: 0.7;
}

/* Button Styles */
.btn-primary-action {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 12px 24px;
    background: var(--primary-color, #f27178);
    color: white;
    border: none;
    border-radius: 10px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.btn-primary-action:hover:not(:disabled) {
    filter: brightness(0.9);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px var(--secondary-color, rgba(242, 113, 120, 0.25));
}

.btn-primary-action:active:not(:disabled) {
    transform: translateY(0);
}

.btn-primary-action:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.btn-secondary-action {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 12px 24px;
    background: #fff;
    color: #4b5563;
    border: 1px solid #d1d5db;
    border-radius: 10px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
}

.btn-secondary-action:hover {
    background: #f9fafb;
    border-color: #9ca3af;
    color: #1f2937;
}

/* Section Styles */
.settings-section {
    margin-bottom: 24px;
}

.section-label {
    display: block;
    font-size: 0.9rem;
    font-weight: 600;
    color: #374151;
    margin-bottom: 10px;
}

.section-label.required::after {
    content: '*';
    color: var(--danger-color, #f27178);
    margin-left: 4px;
}

/* Cover Upload */
.btn-remove-cover-absolute {
    position: absolute;
    top: 12px;
    right: 12px;
    width: 36px;
    height: 36px;
    background: rgba(255, 255, 255, 0.9);
    color: #ef4444;
    border: none;
    border-radius: 50%;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    z-index: 20;
}

.btn-remove-cover-absolute:hover {
    background: #ef4444;
    color: white;
    transform: scale(1.1);
}

/* Tier Selection */
.tier-selection {
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 1.25rem;
}

.tier-list {
    margin-left: 1.5rem;
    padding-top: 1rem;
    border-top: 1px solid #e5e7eb;
    margin-top: 1rem;
}

.tier-instruction {
    color: var(--primary-color, #f27178);
}

.tier-selection .form-check-input:checked {
    background-color: var(--primary-color, #f27178);
    border-color: var(--primary-color, #f27178);
}

.tier-selection .form-check-input:focus {
    border-color: var(--primary-color, #f27178);
    box-shadow: 0 0 0 0.25rem var(--secondary-color, rgba(242, 113, 120, 0.25));
}


/* Preview Card */
.preview-card {
    border: 1px solid #e5e7eb;
    border-radius: 20px !important;
}

.preview-inner {
    padding: 0;
    display: flex;
    justify-content: center;
}

.char-counter {
    font-size: 0.75rem;
    color: #6b7280;
    margin-top: 6px;
}

.action-xl {
    min-width: 140px;
}

/* Override rich text editor height to ensure good UX */
:deep(.o-text-editor__content) {
  min-height: 200px;
  max-height: 60vh;
  overflow-y: auto;
}
</style>
